import React, { useState, useEffect } from 'react'
import { motion } from 'framer-motion'
import { Code, Eye, Activity, CheckCircle, XCircle, Clock } from 'lucide-react'
import { useApiLogs } from '../contexts/ApiLogsContext'

interface ApiRequest {
  url: string
  method: string
  headers: Record<string, string>
  body?: any
  timestamp: string
}

interface ApiResponse {
  status: number
  statusText: string
  headers: Record<string, string>
  data: any
  timestamp: string
}

interface ApiLog {
  request: ApiRequest
  response: ApiResponse | null
  loading: boolean
  error: string | null
}

interface ApiMonitorProps {
  isVisible: boolean
  onClose: () => void
  width?: number
  onWidthChange?: (width: number) => void
  sessionId?: string
  pubUserId?: string
  apiKey?: string
  onGetSessionDetails?: () => void
  sessionDetailsLog?: ApiLog | null
  uspAllOffersChecked?: boolean | null
  className?: string
  hiddenTabs?: Array<'session' | 'general' | 'history' | 'all-logs'>
}

const ApiMonitor: React.FC<ApiMonitorProps> = ({
  isVisible,
  onClose,
  width = 480,
  onWidthChange,
  sessionId,
  pubUserId,
  apiKey,
  onGetSessionDetails,
  sessionDetailsLog,
  uspAllOffersChecked,
  className = '',
  hiddenTabs = []
}) => {
  const [activeTab, setActiveTab] = useState<'session' | 'general' | 'history' | 'all-logs'>('session')
  const [sidebarWidth, setSidebarWidth] = useState(width)
  const [isResizing, setIsResizing] = useState(false)
  const { apiLogs, selectionHistory } = useApiLogs()

  // Get available tabs (exclude hidden ones)
  const availableTabs = (['session', 'general', 'history', 'all-logs'] as const).filter(tab => !hiddenTabs.includes(tab))

  // Filter moments API calls (MomentScience API calls)
  const momentsApiCalls = apiLogs.filter(log => 
    log.action.includes('/native/v2/offers.json') || 
    log.action.includes('MomentScience') ||
    log.action.includes('offers.json')
  )

  // Filter session API calls (USP session-related API calls only, excluding MomentScience offers API)
  const sessionApiCalls = apiLogs.filter(log => 
    (log.action.includes('/sdk/v4/usp/session/') ||
     log.action.includes('Wrap API') ||
     log.action.includes('select.json') ||
     log.action.includes('unselect.json') ||
     log.action.includes('bulk')) &&
    !log.action.includes('/native/v2/offers.json') &&
    !log.action.includes('MomentScience') &&
    !log.action.includes('offers.json')
  )

  useEffect(() => {
    setSidebarWidth(width)
  }, [width])

  // Update active tab if current tab is hidden
  useEffect(() => {
    if (hiddenTabs.includes(activeTab) && availableTabs.length > 0) {
      setActiveTab(availableTabs[0])
    }
  }, [hiddenTabs, activeTab, availableTabs])

  const handleMouseDown = (e: React.MouseEvent) => {
    setIsResizing(true)
    e.preventDefault()
  }

  useEffect(() => {
    const handleMouseMove = (e: MouseEvent) => {
      if (isResizing) {
        const newWidth = window.innerWidth - e.clientX
        const clampedWidth = Math.max(320, Math.min(newWidth, 800))
        setSidebarWidth(clampedWidth)
        onWidthChange?.(clampedWidth)
      }
    }

    const handleMouseUp = () => {
      setIsResizing(false)
    }

    if (isResizing) {
      document.addEventListener('mousemove', handleMouseMove)
      document.addEventListener('mouseup', handleMouseUp)
    }

    return () => {
      document.removeEventListener('mousemove', handleMouseMove)
      document.removeEventListener('mouseup', handleMouseUp)
    }
  }, [isResizing, onWidthChange])

  if (!isVisible) return null

  return (
    <motion.div
      initial={{ x: '100%' }}
      animate={{ x: 0 }}
      exit={{ x: '100%' }}
      transition={{ type: 'spring', stiffness: 300, damping: 30 }}
      className={`fixed right-0 top-0 h-full bg-white dark:bg-gray-900 border-l border-gray-200 dark:border-gray-700 shadow-2xl z-50 flex flex-col ${className}`}
      style={{ width: `${sidebarWidth}px` }}
    >
      {/* Resize Handle */}
      <div
        className={`absolute left-0 top-0 w-1 h-full bg-gray-300 dark:bg-gray-600 hover:bg-pink-500 dark:hover:bg-pink-400 cursor-ew-resize transition-colors z-10 ${
          isResizing ? 'bg-pink-500 dark:bg-pink-400' : ''
        }`}
        onMouseDown={handleMouseDown}
        title="Drag to resize sidebar"
      />
      
      {/* Resize Handle Visual Indicator */}
      <div
        className={`absolute left-0.5 top-1/2 transform -translate-y-1/2 w-0.5 h-8 bg-white dark:bg-gray-800 rounded-full opacity-60 pointer-events-none ${
          isResizing ? 'opacity-100' : ''
        }`}
      />

      <div className="flex flex-col h-full">
        {/* Panel Header */}
        <div className="flex items-center justify-between p-4 border-b border-gray-200 dark:border-gray-700">
          <div className="flex items-center space-x-2">
            <Code className="w-5 h-5 text-pink-600" />
            <h3 className="font-semibold text-gray-900 dark:text-white">API Monitor</h3>
          </div>
          <div className="flex items-center space-x-2">
            <button
              onClick={() => setSidebarWidth(480)}
              className="text-xs text-gray-500 hover:text-gray-700 dark:text-gray-400 dark:hover:text-gray-300 px-2 py-1 hover:bg-gray-100 dark:hover:bg-gray-700 rounded"
              title="Reset to default width"
            >
              Reset
            </button>
            <button
              onClick={onClose}
              className="p-1 hover:bg-gray-100 dark:hover:bg-gray-800 rounded"
            >
              <Eye className="w-5 h-5 text-gray-500" />
            </button>
          </div>
        </div>

        {/* Tab Navigation */}
        <div className="flex border-b border-gray-200 dark:border-gray-700">
          {!hiddenTabs.includes('session') && (
            <button
              onClick={() => setActiveTab('session')}
              className={`flex-1 px-3 py-3 text-sm font-medium transition-colors ${
                activeTab === 'session'
                  ? 'bg-pink-50 dark:bg-pink-900/20 text-pink-600 dark:text-pink-400 border-b-2 border-pink-500'
                  : 'text-gray-600 dark:text-gray-400 hover:text-gray-900 dark:hover:text-white'
              }`}
            >
              Session API
            </button>
          )}
          {!hiddenTabs.includes('general') && (
            <button
              onClick={() => setActiveTab('general')}
              className={`flex-1 px-3 py-3 text-sm font-medium transition-colors ${
                activeTab === 'general'
                  ? 'bg-pink-50 dark:bg-pink-900/20 text-pink-600 dark:text-pink-400 border-b-2 border-pink-500'
                  : 'text-gray-600 dark:text-gray-400 hover:text-gray-900 dark:hover:text-white'
              }`}
            >
              Moments API
            </button>
          )}
          {!hiddenTabs.includes('history') && (
            <button
              onClick={() => setActiveTab('history')}
              className={`flex-1 px-3 py-3 text-sm font-medium transition-colors ${
                activeTab === 'history'
                  ? 'bg-pink-50 dark:bg-pink-900/20 text-pink-600 dark:text-pink-400 border-b-2 border-pink-500'
                  : 'text-gray-600 dark:text-gray-400 hover:text-gray-900 dark:hover:text-white'
              }`}
            >
              Selection History
            </button>
          )}
          {!hiddenTabs.includes('all-logs') && (
            <button
              onClick={() => setActiveTab('all-logs')}
              className={`flex-1 px-3 py-3 text-sm font-medium transition-colors ${
                activeTab === 'all-logs'
                  ? 'bg-pink-50 dark:bg-pink-900/20 text-pink-600 dark:text-pink-400 border-b-2 border-pink-500'
                  : 'text-gray-600 dark:text-gray-400 hover:text-gray-900 dark:hover:text-white'
              }`}
            >
              All Logs
            </button>
          )}
        </div>

        {/* Tab Content */}
        <div className="flex-1 overflow-y-auto p-4">
          {activeTab === 'session' ? (
            <div className="space-y-4">
              {/* Session API Tab Content */}
              
              {/* Illustration/Guide when no session data is available */}
              {!sessionDetailsLog && (
                <div className="bg-gradient-to-br from-blue-50 to-indigo-50 dark:from-blue-900/20 dark:to-indigo-900/20 rounded-xl p-6 border border-blue-200 dark:border-blue-800">
                  <div className="text-center mb-4">
                    <div className="relative mx-auto w-16 h-16 mb-4">
                      {/* Animated pointer illustration */}
                      <div className="absolute inset-0 bg-blue-500 rounded-full opacity-20 animate-ping"></div>
                      <div className="relative w-16 h-16 bg-blue-500 rounded-full flex items-center justify-center">
                        <svg className="w-8 h-8 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 15l-2 5L9 9l11 4-5 2zm0 0l5 5M7.188 2.239l.777 2.897M5.136 7.965l-2.898-.777M13.95 4.05l-2.122 2.122m-5.657 5.656l-2.121 2.122" />
                        </svg>
                      </div>
                    </div>
                    <h4 className="text-lg font-semibold text-blue-900 dark:text-blue-100 mb-2">
                      Discover Session Insights
                    </h4>
                    <p className="text-blue-700 dark:text-blue-300 text-sm mb-4">
                      Click the button below to fetch detailed information about your current session, including campaigns, device info, and user statistics.
                    </p>
                    
                    {/* Feature highlights */}
                    <div className="grid grid-cols-2 gap-3 text-left mb-4">
                      <div className="flex items-center space-x-2 text-xs text-blue-600 dark:text-blue-400">
                        <div className="w-2 h-2 bg-blue-500 rounded-full"></div>
                        <span>Campaign Analytics</span>
                      </div>
                      <div className="flex items-center space-x-2 text-xs text-blue-600 dark:text-blue-400">
                        <div className="w-2 h-2 bg-blue-500 rounded-full"></div>
                        <span>Device Information</span>
                      </div>
                      <div className="flex items-center space-x-2 text-xs text-blue-600 dark:text-blue-400">
                        <div className="w-2 h-2 bg-blue-500 rounded-full"></div>
                        <span>Selection Statistics</span>
                      </div>
                      <div className="flex items-center space-x-2 text-xs text-blue-600 dark:text-blue-400">
                        <div className="w-2 h-2 bg-blue-500 rounded-full"></div>
                        <span>Location Data</span>
                      </div>
                    </div>
                    
                    {/* Animated arrow pointing down */}
                    <div className="flex justify-center">
                      <svg className="w-6 h-6 text-blue-500 animate-bounce" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 14l-7 7m0 0l-7-7m7 7V3" />
                      </svg>
                    </div>
                  </div>
                </div>
              )}

              <div className="text-center">
                <button
                  onClick={onGetSessionDetails}
                  disabled={!sessionId}
                  className="w-full flex items-center justify-center space-x-2 px-4 py-3 bg-blue-600 hover:bg-blue-700 disabled:bg-gray-400 disabled:cursor-not-allowed text-white rounded-lg transition-colors shadow-lg hover:shadow-xl"
                >
                  <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
                  </svg>
                  <span>Get Current Session Details</span>
                  {!sessionDetailsLog && (
                    <span className="ml-2 px-2 py-1 bg-blue-500 text-blue-100 text-xs rounded-full">
                      Try it!
                    </span>
                  )}
                </button>
              </div>

              {/* Session Details Response */}
              {sessionDetailsLog && (
                <div className="space-y-4">
                  {/* Session Statistics */}
                  {sessionDetailsLog.response && 
                   sessionDetailsLog.response.status >= 200 && 
                   sessionDetailsLog.response.status < 300 && 
                   sessionDetailsLog.response.data?.data?.session && (
                    <div className="border-t border-gray-200 dark:border-gray-700 pt-4">
                      <h4 className="font-medium text-gray-900 dark:text-white mb-3">Session Statistics</h4>
                      <div className="grid grid-cols-2 gap-3">
                        {/* Total Campaigns */}
                        <div className="bg-blue-50 dark:bg-blue-900/20 rounded-lg p-3 border border-blue-200 dark:border-blue-800">
                          <div className="text-blue-600 dark:text-blue-400 text-xs font-medium">Total Campaigns</div>
                          <div className="text-blue-900 dark:text-blue-100 text-lg font-bold">
                            {sessionDetailsLog.response.data.data.session.campaigns?.length || 0}
                          </div>
                        </div>
                        
                        {/* Selected Campaigns */}
                        <div className="bg-green-50 dark:bg-green-900/20 rounded-lg p-3 border border-green-200 dark:border-green-800">
                          <div className="text-green-600 dark:text-green-400 text-xs font-medium">Selected</div>
                          <div className="text-green-900 dark:text-green-100 text-lg font-bold">
                            {sessionDetailsLog.response.data.data.session.selected?.length || 0}
                          </div>
                        </div>
                        
                        {/* Device Info */}
                        <div className="bg-purple-50 dark:bg-purple-900/20 rounded-lg p-3 border border-purple-200 dark:border-purple-800">
                          <div className="text-purple-600 dark:text-purple-400 text-xs font-medium">Device</div>
                          <div className="text-purple-900 dark:text-purple-100 text-sm font-medium capitalize">
                            {sessionDetailsLog.response.data.data.session.device || 'Unknown'} / {sessionDetailsLog.response.data.data.session.os || 'Unknown'}
                          </div>
                        </div>
                        
                        {/* Browser */}
                        <div className="bg-orange-50 dark:bg-orange-900/20 rounded-lg p-3 border border-orange-200 dark:border-orange-800">
                          <div className="text-orange-600 dark:text-orange-400 text-xs font-medium">Browser</div>
                          <div className="text-orange-900 dark:text-orange-100 text-sm font-medium capitalize">
                            {sessionDetailsLog.response.data.data.session.browser || 'Unknown'}
                          </div>
                        </div>
                      </div>
                    </div>
                  )}

                  <div className="border-t border-gray-200 dark:border-gray-700 pt-4">
                    <h4 className="font-medium text-gray-900 dark:text-white mb-3">Session Details Response</h4>
                    
                    {sessionDetailsLog.loading ? (
                      <div className="flex items-center justify-center p-8">
                        <div className="flex items-center space-x-2">
                          <Activity className="w-5 h-5 animate-spin text-pink-600" />
                          <span className="text-gray-600 dark:text-gray-400">Loading session details...</span>
                        </div>
                      </div>
                    ) : sessionDetailsLog.error ? (
                      <div className="space-y-3">
                        <div className="bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 rounded-lg p-4">
                          <div className="flex items-center space-x-2 mb-2">
                            <div className="w-2 h-2 bg-red-500 rounded-full"></div>
                            <div className="text-red-600 dark:text-red-400 font-medium">API Call Failed</div>
                          </div>
                          <div className="text-red-700 dark:text-red-300 text-sm mb-3">
                            {sessionDetailsLog.error}
                          </div>
                          <div className="text-xs text-red-600 dark:text-red-400 bg-red-100 dark:bg-red-900/10 rounded p-2">
                            <strong>Common Issues:</strong>
                            <ul className="mt-1 space-y-1 ml-2">
                              <li>• Check if API key is valid</li>
                              <li>• Verify session ID exists</li>
                              <li>• Ensure network connectivity</li>
                              <li>• Check CORS settings</li>
                            </ul>
                          </div>
                        </div>
                        
                        {/* Show response data if available (for HTTP errors) */}
                        {sessionDetailsLog.response && (
                          <div className="bg-gray-50 dark:bg-gray-800 rounded-lg p-3 space-y-3">
                            <div className="flex items-center space-x-2 text-sm">
                              <span className="px-2 py-1 rounded text-xs font-medium bg-red-100 text-red-800 dark:bg-red-900/20 dark:text-red-400">
                                {sessionDetailsLog.response.status}
                              </span>
                              <span className="text-gray-600 dark:text-gray-400">
                                {sessionDetailsLog.response.statusText}
                              </span>
                            </div>
                            
                            <div className="text-xs">
                              <div className="text-gray-500 dark:text-gray-400 mb-1">Error Response:</div>
                              <div className="bg-white dark:bg-gray-900 rounded p-2 font-mono max-h-64 overflow-y-auto">
                                <pre className="whitespace-pre-wrap text-xs">
                                  {JSON.stringify(sessionDetailsLog.response.data, null, 2)}
                                </pre>
                              </div>
                            </div>

                            <div className="text-xs text-gray-500 dark:text-gray-400">
                              Timestamp: {new Date(sessionDetailsLog.response.timestamp).toLocaleString()}
                            </div>
                          </div>
                        )}
                      </div>
                    ) : sessionDetailsLog.response ? (
                      <div className="bg-gray-50 dark:bg-gray-800 rounded-lg p-3 space-y-3">
                        <div className="flex items-center space-x-2 text-sm">
                          <span className={`px-2 py-1 rounded text-xs font-medium ${
                            sessionDetailsLog.response.status >= 200 && sessionDetailsLog.response.status < 300
                              ? 'bg-green-100 text-green-800 dark:bg-green-900/20 dark:text-green-400'
                              : 'bg-red-100 text-red-800 dark:bg-red-900/20 dark:text-red-400'
                          }`}>
                            {sessionDetailsLog.response.status}
                          </span>
                          <span className="text-gray-600 dark:text-gray-400">
                            {sessionDetailsLog.response.statusText}
                          </span>
                        </div>
                        
                        <div className="text-xs">
                          <div className="text-gray-500 dark:text-gray-400 mb-1">Response Data:</div>
                          <div className="bg-white dark:bg-gray-900 rounded p-2 font-mono max-h-64 overflow-y-auto">
                            <pre className="whitespace-pre-wrap text-xs">
                              {JSON.stringify(sessionDetailsLog.response.data, null, 2)}
                            </pre>
                          </div>
                        </div>

                        <div className="text-xs text-gray-500 dark:text-gray-400">
                          Timestamp: {new Date(sessionDetailsLog.response.timestamp).toLocaleString()}
                        </div>
                      </div>
                    ) : null}
                  </div>

                  {/* Session Details cURL Command */}
                  <div className="border-t border-gray-200 dark:border-gray-700 pt-4">
                    <h4 className="font-medium text-gray-900 dark:text-white mb-3">cURL Command</h4>
                    <div className="bg-gray-900 dark:bg-black rounded-lg p-3">
                      <pre className="text-green-400 text-xs font-mono whitespace-pre-wrap">
                        {`curl -X GET \\
  "https://api.adspostx.com/sdk/v4/usp/session/${sessionId}.json?pub_user_id=${pubUserId}" \\
  -H "Authorization: Bearer ${apiKey}"`}
                      </pre>
                    </div>
                  </div>
                </div>
              )}

              {/* Session API Logs */}
              <div className="border-t border-gray-200 dark:border-gray-700 pt-4">
                <h4 className="font-medium text-gray-900 dark:text-white mb-3">Session API Logs</h4>
                {sessionApiCalls.length > 0 ? (
                  <div className="space-y-4">
                    {sessionApiCalls.map((log) => (
                      <div key={log.id} className="border border-gray-200 dark:border-gray-700 rounded-lg p-4 bg-gray-50 dark:bg-gray-800">
                        <div className="flex items-center justify-between mb-3">
                          <div className="flex items-center space-x-2">
                            <span className="text-sm font-medium text-gray-700 dark:text-gray-300">
                              {log.action}
                            </span>
                          </div>
                          <div className="flex items-center space-x-2">
                            {log.loading && (
                              <div className="w-2 h-2 bg-yellow-500 rounded-full animate-pulse"></div>
                            )}
                            {log.error && (
                              <div className="w-2 h-2 bg-red-500 rounded-full"></div>
                            )}
                            {!log.loading && !log.error && log.response && (
                              <div className="w-2 h-2 bg-green-500 rounded-full"></div>
                            )}
                            <span className="text-xs text-gray-500 dark:text-gray-400">
                              {new Date(log.timestamp).toLocaleTimeString()}
                            </span>
                          </div>
                        </div>
                        
                        {/* cURL Request */}
                        <div className="mb-3">
                          <h5 className="font-medium text-gray-900 dark:text-white mb-2 text-sm">cURL Request</h5>
                          <div className="bg-gray-900 dark:bg-black rounded-lg p-3">
                            <pre className="text-green-400 text-xs font-mono whitespace-pre-wrap">
                              {(() => {
                                let curl = `curl -X ${log.request.method} \\\n  "${log.request.url}"`
                                if (log.request.headers) {
                                  Object.entries(log.request.headers).forEach(([key, value]) => {
                                    curl += ` \\\n  -H "${key}: ${value}"`
                                  })
                                }
                                if (log.request.body && typeof log.request.body === 'object') {
                                  curl += ` \\\n  -d '${JSON.stringify(log.request.body, null, 2)}'`
                                }
                                return curl
                              })()}
                            </pre>
                          </div>
                        </div>
                        
                        {/* Response */}
                        {log.loading ? (
                          <div className="bg-yellow-50 dark:bg-yellow-900/20 border border-yellow-200 dark:border-yellow-800 rounded p-3">
                            <div className="flex items-center space-x-2">
                              <div className="w-4 h-4 border-2 border-yellow-500 border-t-transparent rounded-full animate-spin"></div>
                              <span className="text-yellow-700 dark:text-yellow-400 text-sm">Loading...</span>
                            </div>
                          </div>
                        ) : log.error ? (
                          <div className="bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 rounded p-3">
                            <div className="text-sm font-medium text-red-700 dark:text-red-400 mb-1">Error</div>
                            <div className="text-sm text-red-600 dark:text-red-300">{log.error}</div>
                          </div>
                        ) : log.response ? (
                          <div className="space-y-3">
                            <h5 className="font-medium text-gray-900 dark:text-white text-sm">Response</h5>
                            <div className="bg-white dark:bg-gray-900 rounded-lg p-3 border border-gray-200 dark:border-gray-700">
                              <div className="flex items-center space-x-2 mb-3">
                                <span className={`px-2 py-1 rounded text-xs font-medium ${
                                  log.response.status >= 200 && log.response.status < 300
                                    ? 'bg-green-100 text-green-800 dark:bg-green-900/20 dark:text-green-400'
                                    : 'bg-red-100 text-red-800 dark:bg-red-900/20 dark:text-red-400'
                                }`}>
                                  {log.response.status}
                                </span>
                                <span className="text-gray-600 dark:text-gray-400">
                                  {log.response.statusText}
                                </span>
                                <span className="text-xs text-gray-500 dark:text-gray-400 ml-auto">
                                  {log.response.timestamp}
                                </span>
                              </div>
                              
                              <div className="text-xs">
                                <div className="text-gray-500 dark:text-gray-400 mb-1">Response Data:</div>
                                <div className="bg-gray-50 dark:bg-gray-800 rounded p-2 font-mono max-h-64 overflow-y-auto">
                                  <pre className="whitespace-pre-wrap text-xs">
                                    {JSON.stringify(log.response.data, null, 2)}
                                  </pre>
                                </div>
                              </div>
                            </div>
                          </div>
                        ) : null}
                      </div>
                    ))}
                  </div>
                ) : (
                  <div className="flex items-center justify-center h-20">
                    <div className="text-center">
                      <Code className="w-6 h-6 text-gray-400 mx-auto mb-2" />
                      <p className="text-gray-500 dark:text-gray-400 text-sm">No session API calls recorded yet</p>
                    </div>
                  </div>
                )}
              </div>

              {!sessionDetailsLog && (
                <div className="flex items-center justify-center h-32">
                  <div className="text-center">
                    <Code className="w-8 h-8 text-gray-400 mx-auto mb-2" />
                    <p className="text-gray-500 dark:text-gray-400">No session details fetched yet</p>
                  </div>
                </div>
              )}
            </div>
          ) : activeTab === 'general' ? (
            <div className="space-y-4">
              {momentsApiCalls.length > 0 ? (
                <div className="space-y-4">
                  {momentsApiCalls.map((log) => (
                    <div key={log.id} className="border border-gray-200 dark:border-gray-700 rounded-lg p-4">
                      <div className="flex items-center justify-between mb-3">
                        <div className="flex items-center space-x-2">
                          {log.loading ? (
                            <div className="w-2 h-2 bg-yellow-500 rounded-full animate-pulse"></div>
                          ) : log.error ? (
                            <XCircle className="w-4 h-4 text-red-500" />
                          ) : (
                            <CheckCircle className="w-4 h-4 text-green-500" />
                          )}
                          <span className="font-medium text-gray-900 dark:text-white">{log.action}</span>
                        </div>
                        <div className="flex items-center space-x-2">
                          <span className="text-xs text-gray-500 dark:text-gray-400">{log.page}</span>
                          <span className="text-xs text-gray-500 dark:text-gray-400">
                            {new Date(log.timestamp).toLocaleString()}
                          </span>
                        </div>
                      </div>

                      {/* Request Details */}
                      <div className="space-y-3">
                        <div>
                          <h4 className="text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">Request</h4>
                          <div className="bg-gray-50 dark:bg-gray-800 rounded p-3 text-sm">
                            <div className="flex items-center space-x-2 mb-2">
                              <span className="font-mono text-blue-600 dark:text-blue-400">{log.request.method}</span>
                              <span className="font-mono text-gray-700 dark:text-gray-300">{log.request.url}</span>
                            </div>
                            {log.request.body && (
                              <div className="mt-2">
                                <span className="text-xs text-gray-500 dark:text-gray-400">Body:</span>
                                <pre className="mt-1 text-xs bg-gray-100 dark:bg-gray-700 p-2 rounded overflow-x-auto">
                                  {JSON.stringify(log.request.body, null, 2)}
                                </pre>
                              </div>
                            )}
                          </div>
                        </div>

                        {/* Response Details */}
                        {log.response && (
                          <div>
                            <h4 className="text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">Response</h4>
                            <div className="bg-gray-50 dark:bg-gray-800 rounded p-3 text-sm">
                              <div className="flex items-center space-x-2 mb-2">
                                <span className={`font-mono px-2 py-1 rounded text-xs ${
                                  log.response.status >= 200 && log.response.status < 300
                                    ? 'bg-green-100 text-green-800 dark:bg-green-900/20 dark:text-green-400'
                                    : 'bg-red-100 text-red-800 dark:bg-red-900/20 dark:text-red-400'
                                }`}>
                                  {log.response.status}
                                </span>
                                <span className="text-gray-600 dark:text-gray-400">{log.response.statusText}</span>
                              </div>
                              {log.response.data && (
                                <div className="mt-2">
                                  <span className="text-xs text-gray-500 dark:text-gray-400">Data:</span>
                                  <pre className="mt-1 text-xs bg-gray-100 dark:bg-gray-700 p-2 rounded overflow-x-auto max-h-40">
                                    {JSON.stringify(log.response.data, null, 2)}
                                  </pre>
                                </div>
                              )}
                            </div>
                          </div>
                        )}

                        {/* Error Details */}
                        {log.error && (
                          <div>
                            <h4 className="text-sm font-medium text-red-700 dark:text-red-300 mb-2">Error</h4>
                            <div className="bg-red-50 dark:bg-red-900/20 rounded p-3 text-sm">
                              <p className="text-red-600 dark:text-red-400">{log.error}</p>
                            </div>
                          </div>
                        )}

                        {/* Loading State */}
                        {log.loading && (
                          <div className="bg-yellow-50 dark:bg-yellow-900/20 rounded p-3 text-sm">
                            <div className="flex items-center space-x-2">
                              <div className="w-2 h-2 bg-yellow-500 rounded-full animate-pulse"></div>
                              <span className="text-yellow-700 dark:text-yellow-400">Loading...</span>
                            </div>
                          </div>
                        )}
                      </div>
                    </div>
                  ))}
                </div>
              ) : (
                <div className="space-y-6">
                  {/* USP Auto-Selection Status Section */}
                  <div className="bg-gradient-to-r from-blue-50 to-indigo-50 dark:from-blue-900/20 dark:to-indigo-900/20 rounded-lg p-4 border border-blue-200 dark:border-blue-800">
                    <div className="flex items-center space-x-2 mb-3">
                      <div className={`w-3 h-3 rounded-full ${
                        uspAllOffersChecked === true 
                          ? 'bg-green-500' 
                          : uspAllOffersChecked === false 
                            ? 'bg-yellow-500' 
                            : 'bg-gray-400'
                      }`}></div>
                      <h4 className="font-medium text-gray-900 dark:text-white">Auto-Selection Status</h4>
                    </div>
                    
                    <div className="space-y-2 text-sm">
                      <div className="flex items-center justify-between">
                        <span className="text-gray-600 dark:text-gray-400">usp_all_offers_checked:</span>
                        <span className={`font-mono px-2 py-1 rounded text-xs ${
                          uspAllOffersChecked === true 
                            ? 'bg-green-100 text-green-800 dark:bg-green-900/20 dark:text-green-400' 
                            : uspAllOffersChecked === false 
                              ? 'bg-yellow-100 text-yellow-800 dark:bg-yellow-900/20 dark:text-yellow-400' 
                              : 'bg-gray-100 text-gray-800 dark:bg-gray-900/20 dark:text-gray-400'
                        }`}>
                          {uspAllOffersChecked === null || uspAllOffersChecked === undefined ? 'not set' : uspAllOffersChecked.toString()}
                        </span>
                      </div>
                      
                      <div className="mt-2 p-3 bg-white dark:bg-gray-800 rounded border">
                        <div className="text-xs text-gray-500 dark:text-gray-400 mb-1">Impact:</div>
                        <div className="text-sm text-gray-700 dark:text-gray-300">
                          {uspAllOffersChecked === true ? (
                            <div className="flex items-start space-x-2">
                              <div className="w-1.5 h-1.5 bg-green-500 rounded-full mt-2 flex-shrink-0"></div>
                              <div>
                                <strong className="text-green-700 dark:text-green-400">Auto-selection enabled</strong>
                                <br />
                                All available offers are automatically selected when the session loads. The bulk selection API is called immediately after offers are retrieved.
                              </div>
                            </div>
                          ) : uspAllOffersChecked === false ? (
                            <div className="flex items-start space-x-2">
                              <div className="w-1.5 h-1.5 bg-yellow-500 rounded-full mt-2 flex-shrink-0"></div>
                              <div>
                                <strong className="text-yellow-700 dark:text-yellow-400">Manual selection required</strong>
                                <br />
                                Users must manually select individual offers or use the "Select All" button to choose their preferred offers.
                              </div>
                            </div>
                          ) : (
                            <div className="flex items-start space-x-2">
                              <div className="w-1.5 h-1.5 bg-gray-500 rounded-full mt-2 flex-shrink-0"></div>
                              <div>
                                <strong className="text-gray-700 dark:text-gray-400">Status unknown</strong>
                                <br />
                                No API response received yet, or the usp_all_offers_checked setting was not found in the response.
                              </div>
                            </div>
                          )}
                        </div>
                      </div>
                    </div>
                  </div>
                  
                  <div className="flex items-center justify-center h-32">
                    <div className="text-center">
                      <Code className="w-8 h-8 text-gray-400 mx-auto mb-2" />
                      <p className="text-gray-500 dark:text-gray-400">No MomentScience API calls recorded yet</p>
                    </div>
                  </div>
                </div>
              )}
            </div>
          ) : activeTab === 'all-logs' ? (
            <div className="space-y-4">
              {apiLogs.length > 0 ? (
                apiLogs.map((log) => (
                  <div key={log.id} className="border border-gray-200 dark:border-gray-700 rounded-lg p-4">
                    <div className="flex items-center justify-between mb-2">
                      <div className="flex items-center space-x-2">
                        <span className="text-sm font-medium text-gray-700 dark:text-gray-300 capitalize">
                          {log.page.replace('-', ' ')}
                        </span>
                        <span className="text-xs text-gray-500 dark:text-gray-400">
                          {log.action}
                        </span>
                      </div>
                      <div className="flex items-center space-x-2">
                        {log.loading && (
                          <div className="w-2 h-2 bg-yellow-500 rounded-full animate-pulse"></div>
                        )}
                        {log.error && (
                          <div className="w-2 h-2 bg-red-500 rounded-full"></div>
                        )}
                        {!log.loading && !log.error && log.response && (
                          <div className="w-2 h-2 bg-green-500 rounded-full"></div>
                        )}
                        <span className="text-xs text-gray-500 dark:text-gray-400">
                          {new Date(log.timestamp).toLocaleTimeString()}
                        </span>
                      </div>
                    </div>
                    
                    {log.error && (
                      <div className="bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 rounded p-3 mb-3">
                        <div className="text-sm font-medium text-red-700 dark:text-red-400 mb-1">Error</div>
                        <div className="text-sm text-red-600 dark:text-red-300">{log.error}</div>
                      </div>
                    )}
                    
                    {log.response && (
                      <div className="bg-green-50 dark:bg-green-900/20 border border-green-200 dark:border-green-800 rounded p-3 mb-3">
                        <div className="text-sm font-medium text-green-700 dark:text-green-400 mb-1">
                          Response: {log.response.status} {log.response.statusText}
                        </div>
                        <div className="text-xs text-green-600 dark:text-green-300">
                          {log.response.timestamp}
                        </div>
                      </div>
                    )}
                    
                    <div className="text-xs text-gray-500 dark:text-gray-400">
                      {log.request.method} {log.request.url}
                    </div>
                  </div>
                ))
              ) : (
                <div className="flex items-center justify-center h-32">
                  <div className="text-center">
                    <Code className="w-8 h-8 text-gray-400 mx-auto mb-2" />
                    <p className="text-gray-500 dark:text-gray-400">No API logs recorded yet</p>
                  </div>
                </div>
              )}
            </div>
          ) : (
            <div className="space-y-4">
              {selectionHistory.length > 0 ? (
                <div className="space-y-4">
                  {selectionHistory.map((entry, index) => (
                    <div key={`${entry.id}-${index}`} className="bg-gray-50 dark:bg-gray-800 rounded-lg p-4 border border-gray-200 dark:border-gray-700">
                      <div className="flex items-center justify-between mb-3">
                        <div className="flex items-center space-x-2">
                          {entry.status === 'success' ? (
                            <CheckCircle className="w-4 h-4 text-green-500" />
                          ) : entry.status === 'error' ? (
                            <XCircle className="w-4 h-4 text-red-500" />
                          ) : (
                            <Clock className="w-4 h-4 text-yellow-500" />
                          )}
                          <span className="font-medium text-gray-900 dark:text-white">
                            {entry.action === 'bulk_select' ? 'Bulk Select' : 
                             entry.action === 'bulk_unselect' ? 'Bulk Unselect' : 
                             entry.action === 'select' ? 'Select' : 'Unselect'}
                          </span>
                          <span className="text-xs text-gray-500 dark:text-gray-400">
                            {entry.page}
                          </span>
                        </div>
                        <span className="text-xs text-gray-500 dark:text-gray-400">
                          {new Date(entry.timestamp).toLocaleString()}
                        </span>
                      </div>

                      <div className="space-y-2">
                        {entry.perkTitle && (
                          <div className="flex items-center justify-between">
                            <span className="text-sm text-gray-600 dark:text-gray-400">Offer:</span>
                            <span className="text-sm font-medium text-gray-900 dark:text-white">{entry.perkTitle}</span>
                          </div>
                        )}
                        
                        {entry.totalPerks !== undefined && (
                          <div className="flex items-center justify-between">
                            <span className="text-sm text-gray-600 dark:text-gray-400">Total Offers:</span>
                            <span className="text-sm font-medium text-gray-900 dark:text-white">{entry.totalPerks}</span>
                          </div>
                        )}

                        {entry.campaignId && (
                          <div className="flex items-center justify-between">
                            <span className="text-sm text-gray-600 dark:text-gray-400">Campaign ID:</span>
                            <span className="text-sm font-mono text-gray-900 dark:text-white">{entry.campaignId}</span>
                          </div>
                        )}

                        {entry.error && (
                          <div className="mt-2 p-2 bg-red-50 dark:bg-red-900/20 rounded border border-red-200 dark:border-red-800">
                            <p className="text-sm text-red-600 dark:text-red-400">{entry.error}</p>
                          </div>
                        )}

                        {entry.curlCommand && (
                          <div className="mt-3">
                            <h5 className="text-xs font-medium text-gray-700 dark:text-gray-300 mb-1">cURL Command:</h5>
                            <div className="bg-gray-900 dark:bg-black rounded p-2">
                              <pre className="text-green-400 text-xs font-mono whitespace-pre-wrap">{entry.curlCommand}</pre>
                            </div>
                          </div>
                        )}
                      </div>
                    </div>
                  ))}
                </div>
              ) : (
                <div className="flex items-center justify-center h-32">
                  <div className="text-center">
                    <Code className="w-8 h-8 text-gray-400 mx-auto mb-2" />
                    <p className="text-gray-500 dark:text-gray-400">No selection history recorded yet</p>
                  </div>
                </div>
              )}
            </div>
          )}
        </div>
      </div>
    </motion.div>
  )
}

export default ApiMonitor
